import Cookies from 'js-cookie';
import { RegionAPI, RegionEnv, Account, Sponsor, FormData, StripePromotionCode, Currency } from '@/utils/types';

const vatServiceUrl = 'https://prod.ws.archireport.com/vatcalculator' as const;

const basicHeaders = {
  'Content-Type': 'application/json',
  ArchireportContext: 'AR',
  ArchireportOrigin: 'archireportweb',
  AppVersion: '6.0.0',
};

export const getAuthHeaders = () => {
  return {
    ...basicHeaders,
    Authorization: `Bearer ${Cookies.get('token')}`,
    ArchireportOrigin: Cookies.get('ArchireportOrigin') || 'archireportweb',
  };
};

export function getAPIUrl(): string {
  const regionString = Cookies.get('region_user');
  if (regionString) {
    const regionObj = JSON.parse(regionString) as RegionAPI;
    return regionObj.api;
  }

  return 'https://api.archireport.com';
}

export function getWebUrl(): string {
  const regionString = Cookies.get('region_user');
  if (regionString) {
    const regionObj = JSON.parse(regionString) as RegionAPI;
    return regionObj.web;
  }

  return 'https://web.archireport.com';
}

type CreateSubscriptionIntentParams = {
  formData: FormData;
  items: { price: string; quantity: number }[];
  currency: Currency;
  taxRate?: number;
  couponId?: string;
};

export async function createSubscriptionIntent(params: CreateSubscriptionIntentParams): Promise<{
  data: { clientSecret: string; customerId: string; subscriptionId: string };
  status: number;
  error: string | null;
}> {
  const response = await fetch(`${getAPIUrl()}/stripe/create_subscription_intent`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(params),
  });

  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err?.message || err?.raw?.message || 'Failed to create Subscription');
  }

  const data = await response.json();

  return {
    data,
    status: response.status,
    error: response.ok ? null : data.raw?.message || data.message || 'An unknown error occurred',
  };
}

export async function getPaymentIntent(paymentIntentId: string) {
  const response = await fetch(`${getAPIUrl()}/stripe/payment_intent/${paymentIntentId}`, {
    method: 'GET',
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch payment intent status: ${response.statusText}`);
  }

  return (await response.json()) as { status: string };
}

export async function getRegionsForEnv(env: RegionEnv): Promise<RegionAPI[]> {
  const response = await fetch(`https://which.regions-api.archireport.com/region-list?env=${env}`);
  return response.json();
}

export async function getPriceDetails(priceId: string) {
  const response = await fetch(`${getAPIUrl()}/stripe/price/${priceId}`, {
    method: 'GET',
    headers: getAuthHeaders(),
  });

  return response.json();
}

export async function checkVatCode(vatCode: string): Promise<{ valid_vat_id: boolean }> {
  const response = await fetch(`${vatServiceUrl}/check-vat-code?vat_number=${vatCode}`);
  return response.json();
}

export async function getDetectedCountry(): Promise<{ country_code: string }> {
  const response = await fetch(`${vatServiceUrl}/country-code`);
  return response.json();
}

export async function calculateVat(
  amountWithVat: number,
  countryCode: string,
  postCode: string,
  vatCode?: string,
): Promise<{ tax_rate: number }> {
  const url = new URL(`${vatServiceUrl}/calculate`);

  const params = new URLSearchParams({
    netPrice: amountWithVat.toString(),
    country: countryCode,
    postalCode: postCode,
    ...(vatCode ? { vat_number: vatCode } : {}),
  });

  url.search = params.toString();

  const response = await fetch(url);
  return response.json();
}

export async function getStripePromotionCode(couponCode: string): Promise<StripePromotionCode> {
  const response = await fetch(`${getAPIUrl()}/stripe/promotion_code/${encodeURIComponent(couponCode)}`, {
    method: 'GET',
    headers: getAuthHeaders(),
  });
  return (await response.json()) as StripePromotionCode;
}

export async function getAccount(): Promise<Account> {
  const response = await fetch(`${getAPIUrl()}/account`, {
    method: 'GET',
    headers: getAuthHeaders(),
  });
  return response.json();
}

export async function getSponsors(): Promise<Sponsor[]> {
  const response = await fetch(`${getAPIUrl()}/web/sponsorship/is_sponsored`, {
    method: 'GET',
    headers: getAuthHeaders(),
  });
  return response.json();
}

export async function loginWeb(email: string, password: string): Promise<{ token: string }> {
  const response = await fetch(`${getAPIUrl()}/login_web`, {
    method: 'POST',
    headers: basicHeaders,
    body: JSON.stringify({ email, password }),
  });

  if (!response.ok) {
    throw new Error(`Login failed: ${response.statusText}`);
  }

  return response.json();
}
